import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

export const faqData = [
  {
    id: 'faq-1',
    question: 'What does a civil lawyer do?',
    shortAnswer: 'A civil lawyer handles non-criminal legal matters including property disputes, contracts, family matters, and civil rights cases.',
    mediumAnswer: 'A civil lawyer represents clients in non-criminal legal disputes. This includes matters related to property ownership, contractual obligations, family law, business disputes, and civil rights violations. They help clients navigate the legal system through litigation, negotiation, or alternative dispute resolution.',
    detailedAnswer: 'A civil lawyer, also known as a civil litigation attorney, handles legal disputes between individuals, organizations, or entities that do not involve criminal charges. Their work encompasses a wide range of matters including property disputes, breach of contract cases, personal injury claims, family law matters, landlord-tenant disputes, and civil rights issues. Civil lawyers provide legal advice, draft documents, negotiate settlements, and represent clients in court proceedings. In Bihar, civil lawyers frequently appear before District Courts, the Patna High Court, and various tribunals to advocate for their clients\' interests.'
  },
  {
    id: 'faq-2',
    question: 'How long does a civil case take in Bihar?',
    shortAnswer: 'Civil cases in Bihar typically take 2-5 years depending on complexity, court workload, and procedural factors.',
    mediumAnswer: 'The duration of civil cases in Bihar varies significantly based on case complexity, court backlog, and procedural requirements. Simple matters may be resolved within 1-2 years, while complex property or partition suits can take 5 years or more. Appeals to the Patna High Court add additional time to the process.',
    detailedAnswer: 'The timeline for civil litigation in Bihar depends on multiple factors including case complexity, evidence requirements, number of parties involved, court backlogs, and procedural compliance. At the District Court level, civil suits typically take 2-4 years for disposal. Property disputes and partition suits often require longer due to documentation requirements and multiple hearings. Appeals to the Patna High Court can add 2-3 additional years. Recent judicial reforms and digitization efforts aim to reduce these timelines. Parties can explore alternative dispute resolution mechanisms like mediation or Lok Adalats for faster resolution of certain matters.'
  },
  {
    id: 'faq-3',
    question: 'What is the process of filing a civil suit in Patna?',
    shortAnswer: 'Filing a civil suit involves drafting a plaint, paying court fees, submitting documents, and following procedural requirements at the appropriate court.',
    mediumAnswer: 'To file a civil suit in Patna, you need to prepare a plaint stating your claim, attach supporting documents, calculate and pay appropriate court fees, and file at the correct jurisdictional court. The process includes verification, registration, service of summons to defendants, and scheduling of hearings.',
    detailedAnswer: 'Filing a civil suit in Patna involves several systematic steps: First, determine the appropriate court jurisdiction based on subject matter and territorial limits. Prepare a plaint that clearly states the cause of action, facts, and relief sought. Attach all supporting documents including agreements, receipts, or property papers. Calculate court fees based on suit valuation under the Court Fees Act. File the plaint at the filing counter where it will be scrutinized for defects. After registration, summons are issued to defendants who must file their written statement. The case proceeds through stages including framing of issues, evidence recording, arguments, and judgment. Electronic filing facilities are available at many courts in Bihar.'
  },
  {
    id: 'faq-4',
    question: 'What is the difference between civil and criminal law?',
    shortAnswer: 'Civil law resolves private disputes between parties, while criminal law addresses offenses against the state punishable by law.',
    mediumAnswer: 'Civil law deals with disputes between private parties seeking remedies like compensation or injunctions. Criminal law involves prosecution of offenses against society by the state, resulting in penalties like imprisonment or fines. The burden of proof differs - preponderance of evidence in civil cases versus beyond reasonable doubt in criminal matters.',
    detailedAnswer: 'Civil and criminal law represent two fundamental branches of the legal system with distinct purposes and procedures. Civil law governs disputes between individuals or entities concerning rights, obligations, or liabilities. Remedies include monetary compensation, specific performance, injunctions, or declaratory relief. The plaintiff must prove their case by preponderance of evidence. Criminal law addresses acts considered offenses against society and the state. The prosecution must prove guilt beyond reasonable doubt. Consequences include imprisonment, fines, or other penalties. In India, civil matters are governed by the Code of Civil Procedure (CPC) while criminal matters follow the Criminal Procedure Code (CrPC). Some acts may give rise to both civil and criminal liability.'
  },
  {
    id: 'faq-5',
    question: 'How to file a property dispute case in Patna?',
    shortAnswer: 'Property dispute cases require filing a suit with property documents, title deeds, and proper court fee payment at the jurisdictional civil court.',
    mediumAnswer: 'To file a property dispute case in Patna, gather all property documents including sale deeds, mutation records, tax receipts, and survey maps. Engage a civil lawyer to draft the plaint specifying the nature of dispute and relief sought. File at the court with territorial jurisdiction over the property location with appropriate court fees.',
    detailedAnswer: 'Filing a property dispute case in Patna requires systematic preparation. First, collect all relevant documents: sale deeds, title documents, mutation records, property tax receipts, survey/settlement records, and any previous court orders. Identify the nature of dispute - ownership, possession, boundary, encroachment, or partition. Determine jurisdiction based on property value and location. The suit should be filed in the court with territorial jurisdiction over the property. Prepare a detailed plaint with facts, cause of action, and reliefs sought. Calculate court fees based on property value as per the Court Fees Act. Consider filing applications for temporary injunction if urgent protection is needed. The process includes verification, defendant\'s response, framing of issues, evidence through affidavits and cross-examination, arguments, and judgment. Revenue records and registered documents carry significant evidentiary value in property matters.'
  }
];

export const FAQSection = () => {
  return (
    <section 
      id="faq" 
      className="py-20 lg:py-28 bg-[#F8F6F2]"
      data-testid="faq-section"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#C6A34A] uppercase tracking-widest text-sm font-medium mb-3">
            Common Questions
          </p>
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B1C2D] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Frequently Asked Questions
          </h2>
          <div className="w-20 h-1 bg-[#C6A34A] mx-auto mb-6" />
          <p className="text-[#4A5568] max-w-2xl mx-auto">
            General information about civil litigation in Bihar and Patna High Court procedures
          </p>
        </div>

        {/* FAQ Accordion */}
        <Accordion type="single" collapsible className="space-y-4">
          {faqData.map((faq) => (
            <AccordionItem 
              key={faq.id} 
              value={faq.id}
              className="bg-white border border-gray-100 px-6 shadow-sm data-[state=open]:shadow-md data-[state=open]:border-[#C6A34A]/30 transition-all"
              data-testid={`faq-item-${faq.id}`}
            >
              <AccordionTrigger 
                className="text-left text-[#0B1C2D] font-medium hover:text-[#C6A34A] hover:no-underline py-6"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-[#4A5568] pb-6 leading-relaxed">
                <p className="mb-4">{faq.mediumAnswer}</p>
                <details className="text-sm">
                  <summary className="text-[#C6A34A] cursor-pointer hover:underline font-medium">
                    Read detailed explanation
                  </summary>
                  <p className="mt-3 text-[#718096] leading-relaxed">
                    {faq.detailedAnswer}
                  </p>
                </details>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Disclaimer */}
        <p className="text-center text-[#718096] text-xs mt-8">
          This information is provided for general awareness only and should not be considered legal advice.
        </p>
      </div>

      {/* FAQ Schema Markup */}
      <script type="application/ld+json" className="schema-markup" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqData.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.detailedAnswer
            }
          }))
        })
      }} />
    </section>
  );
};
